import {
  ArrowRight,
  Bot,
  Building2,
  CheckCircle2,
  FileCheck2,
  FileText,
  MessageSquare,
  PackageOpen,
  Search,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api, normalizePage } from "../lib/api";
import type {
  Announcement,
  Page,
  Policy,
  PortalConfig,
  PortalContent,
} from "../lib/types";
import { useAsync } from "../lib/useAsync";
import { Empty, ErrorState, Loading } from "../components/State";
import { formatDate } from "../components/Badge";

interface HomeProduct {
  id: string;
  name: string;
  tagline?: string;
  summary?: string;
  category?: string;
  product_category?: string;
  status?: string;
  starting_price_label?: string;
  capabilities?: Array<{ name?: string } | string>;
}

const defaultSteps = [
  { title: "登录并填写资料", subtitle: "申请资料随时保存", icon: FileText },
  { title: "园区审核与补充", subtitle: "审核意见即时通知", icon: FileCheck2 },
  { title: "确认入驻", subtitle: "完成线下确认清单", icon: Building2 },
  { title: "加入创业社区", subtitle: "开通入驻成员权限", icon: MessageSquare },
];
const defaultValues = [
  { title: "政策集中看", subtitle: "按层级与类型精准筛选" },
  { title: "申请线上办", subtitle: "审核进度和意见随时查看" },
  { title: "资源真实连", subtitle: "入驻商户互动与合作" },
];

export function HomePage() {
  const portal = useAsync(() => api<PortalConfig>("/portal"), []);
  const policies = useAsync(
    async () =>
      normalizePage(await api<Page<Policy>>("/policies?page=1&pageSize=3")),
    [],
  );
  const announcements = useAsync(
    async () =>
      normalizePage(
        await api<Page<Announcement>>("/announcements?page=1&pageSize=3"),
      ),
    [],
  );
  const products = useAsync(
    async () =>
      normalizePage(
        await api<Page<HomeProduct>>("/products?page=1&pageSize=3"),
      ),
    [],
  );
  const contents = portal.data?.contents || [];
  const section = (name: string) =>
    contents.filter((item) => item.section === name);
  const hero = section("hero")[0];
  const configuredValues = section("value_props");
  const configuredSteps = section("process_steps");
  const admission = section("admission_benefits")[0];
  const highlights = section("service_highlights");
  const faq = section("faq");
  const cta = section("cta")[0];
  const values = configuredValues.length ? configuredValues : defaultValues;
  const steps = configuredSteps.length ? configuredSteps : defaultSteps;

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-content">
          <span className="hero-kicker">
            {portal.data?.park.short_name || "上诚创新港"} · OPC 创业者社区
          </span>
          <h1>{hero?.title || "让一个人的事业，拥有一座园区的支持"}</h1>
          {hero?.body_html ? (
            <ManagedHTML html={hero.body_html} />
          ) : (
            <p>
              {hero?.subtitle ||
                portal.data?.branding.support_text ||
                "集中获取创业政策、在线办理园区入驻，并与真实创业者连接协作。"}
            </p>
          )}
          <div className="hero-actions">
            <ManagedLink
              className="primary-button hero-primary"
              href={hero?.link_url || "/application"}
            >
              {hero?.link_label || "开始入驻申请"}
              <ArrowRight size={17} />
            </ManagedLink>
            <Link className="secondary-button hero-secondary" to="/policies">
              <Search size={17} />
              查看政策
            </Link>
          </div>
          <div className="hero-trust">
            <span>
              <ShieldCheck size={14} />
              全过程状态留痕
            </span>
            <span>
              <CheckCircle2 size={14} />
              资料可随时保存
            </span>
          </div>
        </div>
        <div className="hero-process">
          <header>
            <span>入驻服务流程</span>
            <strong>从申请到服务开通</strong>
          </header>
          {steps.slice(0, 6).map((step, index) => {
            const configured = "code" in step;
            const Icon = defaultSteps[index % defaultSteps.length].icon;
            return (
              <div key={configured ? step.id : step.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <Icon size={17} />
                <strong>{step.title}</strong>
              </div>
            );
          })}
          <Link to="/application">
            查看我的办理进度
            <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <section className="proof-strip">
        {values.slice(0, 4).map((item, index) => (
          <div
            className="proof-item"
            key={"code" in item ? item.id : item.title}
          >
            {index > 0 && <i />}
            <div>
              <strong>{item.title}</strong>
              <span>{item.subtitle || "园区创业服务"}</span>
            </div>
          </div>
        ))}
      </section>

      <section className="public-section home-products-section">
        <div className="section-heading product-heading">
          <div>
            <span>AI PRODUCT PLATFORM</span>
            <h2>为自己的企业创建 AI 应用</h2>
            <p>选择成熟产品，连接本企业飞书，所有应用统一使用平台余额。</p>
          </div>
          <div className="product-heading-facts">
            <span><Bot size={15} />企业专属应用</span>
            <span><WalletCards size={15} />统一平台余额</span>
          </div>
        </div>
        {products.loading ? (
          <Loading label="正在加载 AI 产品" />
        ) : products.error ? (
          <ErrorState error={products.error} retry={products.retry} />
        ) : !products.data?.items.length ? (
          <Empty title="暂无已发布产品" detail="产品完成审核后将在这里展示。" />
        ) : (
          <div className="home-product-grid">
            {products.data.items.map((product, index) => (
              <Link className="home-product-card" to={`/products/${product.id}`} key={product.id}>
                <header>
                  <span className={`product-mark mark-${index + 1}`}>
                    <PackageOpen size={20} />
                  </span>
                  <small>{product.product_category || product.category || "企业 AI"}</small>
                </header>
                <h3>{product.name}</h3>
                <p>{product.tagline || product.summary || "为企业工作流提供可控、可审计的 AI 能力。"}</p>
                <footer>
                  <span>{product.starting_price_label || "按实际用量扣减余额"}</span>
                  <ArrowRight size={16} />
                </footer>
              </Link>
            ))}
          </div>
        )}
        <Link className="text-link" to="/products">
          查看全部 AI 产品
          <ArrowRight size={15} />
        </Link>
      </section>

      <section className="public-section">
        <div className="section-heading">
          <span>POLICY SERVICE</span>
          <h2>近期政策</h2>
          <p>按置顶优先、发布时间倒序展示。</p>
        </div>
        {policies.loading ? (
          <Loading />
        ) : policies.error ? (
          <ErrorState error={policies.error} retry={policies.retry} />
        ) : !policies.data?.items.length ? (
          <Empty title="暂无已发布政策" />
        ) : (
          <div className="preview-grid">
            {policies.data.items.map((policy) => (
              <Link
                className="preview-card"
                to={`/policies/${policy.id}`}
                key={policy.id}
              >
                <div>
                  <span>{policy.level || "政策"}</span>
                  {policy.is_pinned && <b>置顶</b>}
                </div>
                <h3>{policy.title}</h3>
                <p>{policy.summary || "查看政策适用条件和申报详情。"}</p>
                <footer>
                  <span>
                    {policy.issuer || policy.source || "园区服务中心"}
                  </span>
                  <span>{formatDate(policy.published_at)}</span>
                </footer>
              </Link>
            ))}
          </div>
        )}
        <Link className="text-link" to="/policies">
          查看全部政策
          <ArrowRight size={15} />
        </Link>
      </section>

      <section className="home-band">
        <div>
          <span>SETTLEMENT SERVICE</span>
          <h2>{admission?.title || "一份申请，清晰走完入驻流程"}</h2>
          {admission?.body_html ? (
            <ManagedHTML html={admission.body_html} />
          ) : (
            <p>
              {admission?.subtitle ||
                "草稿自动保留；退回补充后可继续修改；每次审核和状态变化都在时间线中清晰记录。"}
            </p>
          )}
          <ManagedLink
            className="primary-button light"
            href={admission?.link_url || "/application"}
          >
            {admission?.link_label || "填写申请"}
            <ArrowRight size={16} />
          </ManagedLink>
        </div>
        <ol>
          {steps.slice(0, 4).map((step, index) => (
            <li key={"code" in step ? step.id : step.title}>
              <span>{index + 1}</span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.subtitle || "查看当前阶段和处理结果"}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {highlights.length > 0 && (
        <ManagedSection eyebrow="PARK SERVICES" items={highlights} />
      )}

      <section className="public-section">
        <div className="section-heading">
          <span>PARK ANNOUNCEMENTS</span>
          <h2>园区最新动态</h2>
          <p>查看园区通知并进入创业者社区。</p>
        </div>
        {announcements.loading ? (
          <Loading />
        ) : announcements.error ? (
          <ErrorState error={announcements.error} retry={announcements.retry} />
        ) : !announcements.data?.items.length ? (
          <Empty title="暂无园区公告" />
        ) : (
          <div className="post-preview">
            {announcements.data.items.map((item) => (
              <Link key={item.id} to="/community">
                <span>{item.is_pinned ? "置顶公告" : "园区公告"}</span>
                <strong>{item.title}</strong>
                <small>{formatDate(item.published_at)}</small>
              </Link>
            ))}
          </div>
        )}
        <Link className="text-link" to="/community">
          进入创业社区
          <ArrowRight size={15} />
        </Link>
      </section>

      {faq.length > 0 && <ManagedSection eyebrow="FAQ" items={faq} />}
      {cta && (
        <section className="managed-cta">
          <div>
            <span>START HERE</span>
            <h2>{cta.title}</h2>
            {cta.body_html ? (
              <ManagedHTML html={cta.body_html} />
            ) : (
              cta.subtitle && <p>{cta.subtitle}</p>
            )}
          </div>
          {cta.link_url && cta.link_label && (
            <ManagedLink className="primary-button" href={cta.link_url}>
              {cta.link_label}
              <ArrowRight size={16} />
            </ManagedLink>
          )}
        </section>
      )}
    </div>
  );
}

function ManagedSection({
  eyebrow,
  items,
}: {
  eyebrow: string;
  items: PortalContent[];
}) {
  return (
    <section className="public-section managed-section">
      <div className="section-heading">
        <span>{eyebrow}</span>
        <h2>{items[0]?.section === "faq" ? "常见问题" : "园区服务"}</h2>
      </div>
      <div className="managed-grid">
        {items.map((item) => (
          <article key={item.id}>
            <h3>{item.title}</h3>
            {item.subtitle && <p>{item.subtitle}</p>}
            {item.body_html && <ManagedHTML html={item.body_html} />}
            {item.link_url && item.link_label && (
              <ManagedLink className="text-link" href={item.link_url}>
                {item.link_label}
                <ArrowRight size={14} />
              </ManagedLink>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function ManagedHTML({ html }: { html: string }) {
  return (
    <div
      className="managed-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function ManagedLink({
  href,
  className,
  children,
}: {
  href: string;
  className: string;
  children: React.ReactNode;
}) {
  if (href.startsWith("https://")) {
    return (
      <a className={className} href={href} rel="noreferrer" target="_blank">
        {children}
      </a>
    );
  }
  return (
    <Link className={className} to={href}>
      {children}
    </Link>
  );
}
